import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  CHART_PALETTE, CHART_GRID_COLOR, CHART_AXIS_TICK_STYLE,
} from './chartPalette';
import { CustomChartTooltip } from './CustomChartTooltip';

type HeroMetric = 'followersGained' | 'reachThisSeason' | 'postsMadeThisSeason';

interface SeriesPoint { snapshotDate: string; value: number; }
interface SeriesItem {
  cognitoSub: string;
  fullName: string;
  instagramUsername: string;
  points: SeriesPoint[];
}

interface SeasonGrowthHeroChartProps {
  allSeries: SeriesItem[];
  metric: HeroMetric;
  onMetricChange: (m: HeroMetric) => void;
}

const METRICS: { key: HeroMetric; label: string }[] = [
  { key: 'followersGained', label: 'Followers Gained' },
  { key: 'reachThisSeason', label: 'Reach' },
  { key: 'postsMadeThisSeason', label: 'Posts' },
];

/** Merges per-contestant series arrays into a single wide-format dataset. */
function mergeSeries(series: SeriesItem[]) {
  const byDate = new Map<string, Record<string, number | string>>();
  for (const s of series) {
    for (const pt of s.points) {
      if (!byDate.has(pt.snapshotDate)) byDate.set(pt.snapshotDate, { snapshotDate: pt.snapshotDate });
      byDate.get(pt.snapshotDate)![s.cognitoSub] = pt.value;
    }
  }
  return Array.from(byDate.values()).sort((a, b) => String(a.snapshotDate).localeCompare(String(b.snapshotDate)));
}

export function SeasonGrowthHeroChart({ allSeries, metric, onMetricChange }: SeasonGrowthHeroChartProps) {
  const data = mergeSeries(allSeries);
  const top5 = allSeries.slice(0, 5);
  return (
    <div className="bg-white rounded-card shadow-subtle border border-gold-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-gold-950 text-lg">Top 5 Growth</h3>
        <div className="inline-flex border border-gold-100 rounded overflow-hidden" role="tablist">
          {METRICS.map((m, i) => (
            <button
              key={m.key}
              type="button"
              role="tab"
              aria-selected={metric === m.key}
              onClick={() => onMetricChange(m.key)}
              className={`px-4 py-2 min-h-[var(--touch-target-min)] text-eyebrow-sm font-extrabold
                ${metric === m.key ? 'bg-gold-950 text-gold-50' : 'bg-white text-gold-700 hover:bg-gold-50'}
                ${i < METRICS.length - 1 ? 'border-r border-gold-100' : ''}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <div className="text-center py-8 text-eyebrow-sm font-extrabold text-gold-500">
          No growth data yet
        </div>
      ) : (
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />
              <XAxis dataKey="snapshotDate" tick={CHART_AXIS_TICK_STYLE} stroke={CHART_GRID_COLOR} />
              <YAxis tick={CHART_AXIS_TICK_STYLE} stroke={CHART_GRID_COLOR} />
              <Tooltip content={<CustomChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#a48b3c' }} />
              {top5.map((s, i) => (
                <Line
                  key={s.cognitoSub}
                  type="monotone"
                  dataKey={s.cognitoSub}
                  name={s.fullName || s.instagramUsername}
                  stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
